import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 px-6 bg-deep border-t border-white/5 text-center" data-id="element-34">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8" data-id="element-35">
          <span className="text-muted text-sm" data-id="element-36">
            © {new Date().getFullYear()} BioClock™. All rights reserved.
          </span>
          <div className="flex gap-6" data-id="element-37">
            <button onClick={() => setActiveModal('privacy')} className="text-muted text-sm hover:text-amber transition-colors" data-id="element-38">
              Privacy Policy
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-muted text-sm hover:text-amber transition-colors" data-id="element-39">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-40">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-41">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-surface border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-42">
              <div className="flex justify-between items-center p-6 border-b border-white/10" data-id="element-43">
                <h2 className="text-xl font-bold text-white" data-id="element-44">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="text-muted hover:text-white transition-colors p-1" data-id="element-45">
                  <X className="w-5 h-5" data-id="element-46" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-body text-sm leading-relaxed space-y-4" data-id="element-47">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-48" /> : <TermsContent data-id="element-49" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-muted text-xs" data-id="element-50">Last updated: February 2026</p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-51">
        1. Information We Collect
      </h3>
      <p data-id="element-52">
        When you purchase the Canine BioClock Protocol, we collect your name,
        email address, and payment information. Payment processing is handled by
        secure third-party processors — we never store your credit card details.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-53">
        2. How We Use Your Information
      </h3>
      <p data-id="element-54">
        We use your information solely to deliver your digital product, send
        purchase confirmations, and provide customer support. We may also send
        occasional product updates, which you can opt out of at any time.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-55">3. Data Sharing</h3>
      <p data-id="element-56">
        We do not sell, rent, or share your personal information with third
        parties for marketing purposes. Data is only shared with essential
        service providers (payment processing, email delivery) required to
        fulfill your order.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-57">4. Cookies</h3>
      <p data-id="element-58">
        Our website uses essential cookies to maintain site functionality and
        analytics cookies to understand how visitors interact with our pages.
        You can disable cookies through your browser settings.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-59">5. Data Security</h3>
      <p data-id="element-60">
        We implement industry-standard SSL encryption and security measures to
        protect your personal data. However, no method of electronic
        transmission is 100% secure.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-61">6. Your Rights</h3>
      <p data-id="element-62">
        You have the right to access, correct, or delete your personal data at
        any time. To exercise these rights, contact our support team via the
        email provided in your purchase confirmation.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-muted text-xs" data-id="element-63">Last updated: February 2026</p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-64">
        1. Product Description
      </h3>
      <p data-id="element-65">
        The Canine BioClock Protocol is a digital information product delivered
        as a PDF. It is intended for educational purposes only and does not
        constitute veterinary advice. Always consult a qualified veterinarian
        before making changes to your dog's diet.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-66">
        2. Digital Delivery
      </h3>
      <p data-id="element-67">
        Upon successful payment, you will receive instant access to the digital
        protocol and all bonus materials. Delivery is electronic — no physical
        products will be shipped.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-68">3. Refund Policy</h3>
      <p data-id="element-69">
        We offer a 60-day "More Tail Wags" money-back guarantee. If you are not
        satisfied with the product for any reason, contact us within 60 days of
        purchase for a full refund. You may keep all materials.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-70">
        4. Intellectual Property
      </h3>
      <p data-id="element-71">
        All content within the Canine BioClock Protocol is protected by
        copyright. You may not reproduce, distribute, or resell any part of the
        protocol without written permission.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-72">5. Disclaimer</h3>
      <p data-id="element-73">
        Results described in our materials are based on research and individual
        testimonials. Individual results may vary. The Canine BioClock Protocol
        is not a substitute for professional veterinary care.
      </p>

      <h3 className="text-white font-bold text-base mt-4" data-id="element-74">
        6. Limitation of Liability
      </h3>
      <p data-id="element-75">
        BioClock™ shall not be held liable for any damages arising from the use
        or inability to use the product. Our total liability is limited to the
        purchase price paid.
      </p>
    </>;
}